import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'

import AutoHideTooltip from './AutoHideTooltip'
import styles from '../../ProjectStoryMapBoard.module.css'

const BOARD_STAT_ITEMS = [
    { key: 'journeys', singular: 'journey', plural: 'journeys' },
    { key: 'steps', singular: 'step', plural: 'steps' },
    { key: 'releases', singular: 'release', plural: 'releases' },
    { key: 'stories', singular: 'story', plural: 'stories' },
]

const DEFAULT_STORY_MAP_TITLE = 'Untitled Story Map'
const MAX_TITLE_DISPLAY_LENGTH = 42

const truncateTitle = (value = '', maxLength = MAX_TITLE_DISPLAY_LENGTH) => {
    const text = String(value).trim()
    if (text.length <= maxLength) {
        return { display: text, isTruncated: false }
    }

    return {
        display: `${text.slice(0, maxLength).trimEnd()}…`,
        isTruncated: true,
    }
}

const BoardHeader = ({
    title,
    boardStats,
    onBack,
    onCommitTitle,
    onCopyLink,
    variant = 'default',
    readOnly = false,
}) => {
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(title || DEFAULT_STORY_MAP_TITLE)
    const inputRef = useRef(null)

    useEffect(() => {
        if (!isEditing) {
            setDraft(title || DEFAULT_STORY_MAP_TITLE)
        }
    }, [title, isEditing])

    useEffect(() => {
        if (!isEditing) return undefined

        const frame = window.requestAnimationFrame(() => {
            inputRef.current?.focus()
            inputRef.current?.select()
        })

        return () => window.cancelAnimationFrame(frame)
    }, [isEditing])

    const commitTitle = useCallback(() => {
        const nextTitle = draft.trim() || DEFAULT_STORY_MAP_TITLE
        setDraft(nextTitle)
        setIsEditing(false)

        if (nextTitle !== (title || DEFAULT_STORY_MAP_TITLE)) {
            onCommitTitle?.(nextTitle)
        }
    }, [draft, onCommitTitle, title])

    const startEditing = () => {
        if (readOnly) return
        setDraft(title || DEFAULT_STORY_MAP_TITLE)
        setIsEditing(true)
    }

    const handleTitleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            commitTitle()
        }
        if (event.key === 'Escape') {
            setDraft(title || DEFAULT_STORY_MAP_TITLE)
            setIsEditing(false)
        }
    }

    const fullTitle = title || DEFAULT_STORY_MAP_TITLE
    const { display: visibleTitle, isTruncated } = truncateTitle(fullTitle)

    return (
        <div
            className={`${styles.boardHeader} ${
                variant === 'tabBar' ? styles.boardHeaderTabBar : ''
            }`}
        >
            <AutoHideTooltip title="Back to story maps" placement="bottom">
                <button
                    type="button"
                    className={styles.backButton}
                    onClick={onBack}
                    aria-label="Back to story maps"
                >
                    <Icon icon="mdi:arrow-left" width={18} />
                </button>
            </AutoHideTooltip>

            <div className={styles.boardHeaderContent}>
                {isEditing ? (
                    <input
                        ref={inputRef}
                        type="text"
                        className={styles.boardTitleInput}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onBlur={commitTitle}
                        onKeyDown={handleTitleKeyDown}
                        aria-label="Story map name"
                    />
                ) : (
                    <AutoHideTooltip title={isTruncated ? fullTitle : null}>
                        <span className={styles.boardTitleWrap}>
                            {readOnly ? (
                                <span className={styles.boardTitleBtn}>{visibleTitle}</span>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.boardTitleBtn}
                                    onClick={startEditing}
                                    aria-label={`Edit story map name: ${fullTitle}`}
                                >
                                    {visibleTitle}
                                </button>
                            )}
                        </span>
                    </AutoHideTooltip>
                )}

                {onCopyLink ? (
                    <AutoHideTooltip title="Copy link" placement="bottom">
                        <button
                            type="button"
                            className={styles.boardCopyLinkBtn}
                            onClick={onCopyLink}
                            aria-label="Copy story map link"
                        >
                            <Icon icon="mdi:link-variant" width={16} />
                        </button>
                    </AutoHideTooltip>
                ) : null}

                <span className={styles.boardHeaderDivider} aria-hidden="true" />

                <div className={styles.boardStats}>
                    {BOARD_STAT_ITEMS.map(({ key, singular, plural }, index) => {
                        const count = boardStats[key]
                        const label = count === 1 ? singular : plural

                        return (
                            <span key={key} className={styles.boardStatItem}>
                                {index > 0 ? (
                                    <span className={styles.boardStatSep} aria-hidden="true">
                                        ·
                                    </span>
                                ) : null}
                                <span className={styles.boardStatValue}>{count}</span>
                                <span className={styles.boardStatLabel}>{label}</span>
                            </span>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default BoardHeader
